import React, { useState, useEffect, useMemo } from "react";
import API from "../../api/api";
import { FaPhoneAlt, FaEnvelope, FaSearch, FaUserTie, FaMapMarkerAlt, FaInfoCircle } from "react-icons/fa";

const DistrictContacts = () => {
  const [data, setData] = useState({ districts: [] });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    API.fetchDistrictContacts()
      .then((res) => {
        setData({
          districts: res?.districts || [],
        });
      })
      .catch((err) => {
        console.error("Failed to fetch district contacts:", err);
      })
      .finally(() => setLoading(false));
  }, []);

  const filteredDistricts = useMemo(() => {
    if (!searchTerm.trim()) return data.districts;
    const q = searchTerm.toLowerCase().trim();
    return data.districts.filter(
      (d) =>
        (d.district_name && d.district_name.toLowerCase().includes(q)) ||
        (d.official_name && d.official_name.toLowerCase().includes(q)) ||
        (d.mobile && String(d.mobile).includes(q)) ||
        (d.email && d.email.toLowerCase().includes(q))
    );
  }, [data.districts, searchTerm]);

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
          District Associations &amp; Official Contacts
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Directory of District Para Sports Association Presidents and Secretaries.
        </p>
      </div>

      {/* Mandatory Notice Banner */}
      <div className="bg-gradient-to-br from-amber-500/10 via-amber-50 to-orange-50 border border-amber-300 p-5 rounded-2xl shadow-xs">
        <div className="flex items-start gap-3.5">
          <div className="p-2.5 bg-amber-500 text-white rounded-xl shadow-xs shrink-0 mt-0.5">
            <FaInfoCircle className="text-xl" />
          </div>
          <div className="space-y-1">
            <h2 className="text-base font-bold text-amber-950">
              Mandatory Prior Intimation Notice
            </h2>
            <p className="text-xs sm:text-sm text-amber-900 leading-relaxed">
              Before registering as a player or applying for any championship/event, all athletes{" "}
              <strong>must contact their corresponding District Secretary or President</strong> for prior intimation.
              District authorization is strictly mandatory as district officials must review and approve your registration.
              Registrations submitted without prior intimation to your district association will not be accepted.
            </p>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder="Search by District name, Secretary name, or contact number..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-2xl text-sm font-medium focus:ring-2 focus:ring-indigo-500 focus:outline-none shadow-xs transition"
        />
      </div>

      {/* District Cards */}
      {loading ? (
        <div className="py-20 text-center text-slate-400 font-medium">
          Loading district directory...
        </div>
      ) : filteredDistricts.length === 0 ? (
        <div className="py-20 text-center text-slate-500 bg-white border border-slate-200 rounded-2xl p-8">
          No matching district found. Try searching for another district or keyword.
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-3 px-1">
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">
              Districts of Andhra Pradesh ({filteredDistricts.length})
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredDistricts.map((item) => (
              <div
                key={item.district_id || item.district_name}
                className="bg-white border border-slate-200 hover:border-indigo-400 hover:shadow-md p-5 rounded-2xl transition-all duration-200 flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between pb-3 border-b border-slate-100 mb-3">
                    <span className="font-bold text-base text-slate-900 flex items-center gap-2">
                      <FaMapMarkerAlt className="text-rose-500 shrink-0" />
                      {item.district_name}
                    </span>
                    <span className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700">
                      {item.state || "AP"}
                    </span>
                  </div>

                  <div className="space-y-1.5 mb-4">
                    <div className="flex items-start gap-2">
                      <FaUserTie className="text-slate-400 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-bold text-slate-800">
                          {item.official_name || "District Secretary"}
                        </p>
                        <p className="text-xs text-slate-500">
                          {item.designation || "District Secretary / President"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-3 border-t border-slate-100 flex items-center gap-2 mt-auto">
                  {item.mobile ? (
                    <a
                      href={`tel:${item.mobile}`}
                      className="flex-1 py-2 px-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition shadow-xs"
                    >
                      <FaPhoneAlt className="text-xs" /> {item.mobile}
                    </a>
                  ) : (
                    <span className="flex-1 text-center py-2 text-xs text-slate-400 italic">
                      Contact via State Office
                    </span>
                  )}
                  {item.email && (
                    <a
                      href={`mailto:${item.email}`}
                      title={item.email}
                      className="py-2 px-3.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-semibold flex items-center justify-center transition"
                    >
                      <FaEnvelope />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DistrictContacts;
